import { api } from '@web/api/client'
import type { LoginProvider } from '@web/api/types'
import { CanopyLogo } from '@web/components/CanopyLogo'
import { CanopyMark } from '@web/components/CanopyMark'
import { OpeningState } from '@web/components/OpeningState'
import { Icon } from '@web/components/ui/icons'
import { DocumentTitle } from '@web/lib/documentTitle'
import {
  navigateWorkspace,
  type SsoCallbackFailure,
  useIdentity,
} from '@web/state/identity'
import { type ReactNode, useEffect, useRef, useState } from 'react'

export function IdentityImage({
  src,
  name,
  organization = false,
}: {
  src?: string
  name: string
  organization?: boolean
}) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [src])
  return (
    <span
      className={`identity-image ${organization ? 'identity-image-org' : ''}`}
    >
      {src && !failed ? (
        <img src={src} alt="" onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">
          {name
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part[0])
            .join('')}
        </span>
      )}
    </span>
  )
}

function WorkspaceBrand() {
  return (
    <a className="identity-brand" href="/" aria-label="Canopy home">
      <CanopyMark />
      <strong>Canopy</strong>
    </a>
  )
}

function LoginFooter() {
  return (
    <p className="identity-screen-footer">
      {document.documentElement.dataset.desktop ? (
        <button
          type="button"
          className="identity-app-settings"
          onClick={() => window.dispatchEvent(new Event('canopy:app-settings'))}
        >
          <CanopyLogo size={17} transparent />
          <span>App Settings</span>
        </button>
      ) : (
        'Canopy by Oberon'
      )}
    </p>
  )
}

export function IdentityGate({ children }: { children: ReactNode }) {
  const identity = useIdentity()
  if (identity.loading)
    return (
      <>
        <DocumentTitle>Opening workspace</DocumentTitle>
        <OpeningState
          className="identity-loading"
          title="Opening your workspace"
          description="Confirming your account and workspace access."
        />
      </>
    )
  if (identity.ssoFailure)
    return <SsoFailureScreen failure={identity.ssoFailure} />
  if (!identity.state)
    return (
      <>
        <DocumentTitle>Workspace unavailable</DocumentTitle>
        <main className="identity-screen">
          <WorkspaceBrand />
          <section className="identity-card identity-connection-error">
            <Icon name="offline" size={30} />
            <h1>We couldn’t connect.</h1>
            <p role="alert">
              {identity.error || 'The workspace is temporarily unavailable.'}
            </p>
            <button
              className="identity-primary"
              onClick={() => void identity.reload()}
            >
              Try again
            </button>
          </section>
        </main>
      </>
    )
  if (!identity.state.current_user || identity.addingAccount)
    return (
      <>
        <DocumentTitle>Sign in</DocumentTitle>
        <LoginScreen />
      </>
    )
  if (!identity.state.current_org)
    return (
      <>
        <DocumentTitle>Choose organization</DocumentTitle>
        <OrganizationScreen />
      </>
    )
  if (identity.busy && !identity.savingPreferences)
    return (
      <>
        <DocumentTitle>Switching workspace</DocumentTitle>
        <OpeningState
          className="identity-loading"
          title="Switching your workspace"
          description="Loading the agents and sessions available to you."
        />
      </>
    )
  return (
    <>
      {(identity.error || identity.ssoNotice) && (
        <div className="identity-global-error" role="alert">
          <span>{identity.error || identity.ssoNotice}</span>
          <button
            onClick={() => {
              identity.dismissError()
              identity.dismissSsoNotice()
            }}
            aria-label="Dismiss message"
          >
            <Icon name="close" />
          </button>
        </div>
      )}
      {children}
    </>
  )
}

function SsoFailureScreen({ failure }: { failure: SsoCallbackFailure }) {
  const identity = useIdentity()
  const title =
    failure.status === 'no_account'
      ? 'No Canopy account for this email'
      : failure.status === 'email_mismatch'
        ? 'Google email does not match'
        : failure.status === 'link_pending'
          ? 'Account approval needed'
          : 'Google sign-in could not be completed'
  return (
    <>
      <DocumentTitle>{title}</DocumentTitle>
      <main className="identity-screen identity-login sso-screen sso-google">
        <div className="identity-screen-center">
          <section className="identity-card sso-card sso-result-card">
            <ProviderLogo provider="google" />
            <h1>{title}</h1>
            {failure.status === 'no_account' ? (
              <>
                <p className="sso-result-detail" role="alert">
                  Google sign-in succeeded, but there is no Canopy account for{' '}
                  {failure.email ? (
                    <strong>{failure.email}</strong>
                  ) : (
                    'that email'
                  )}
                  .
                </p>
                <p className="sso-result-detail">
                  Contact your organization’s registered administrator
                  {failure.adminContact ? (
                    <>
                      {' '}
                      at{' '}
                      <a href={`mailto:${failure.adminContact}`}>
                        {failure.adminContact}
                      </a>
                    </>
                  ) : null}{' '}
                  to request an account.
                </p>
              </>
            ) : failure.status === 'email_mismatch' ? (
              <p className="sso-result-detail" role="alert">
                The Google account you used does not match{' '}
                {failure.expectedEmail ? (
                  <strong>{failure.expectedEmail}</strong>
                ) : (
                  'the email you entered'
                )}
                .{' '}
                {failure.actualEmail ? (
                  <>
                    Google returned <strong>{failure.actualEmail}</strong>.{' '}
                  </>
                ) : null}
                Sign in with the matching Google account or try another Canopy
                email.
              </p>
            ) : failure.status === 'link_pending' ? (
              <p className="sso-result-detail" role="alert">
                Your Google account was verified, but an administrator must
                approve its link to your Canopy account. Try signing in again
                after approval.
              </p>
            ) : (
              <p className="sso-result-detail" role="alert">
                Google sign-in did not finish. Please try again. If the problem
                continues, contact your organization’s administrator.
              </p>
            )}
            <a
              className="sso-result-retry"
              href="/"
              onClick={(event) => {
                event.preventDefault()
                identity.retrySsoWithDifferentAccount()
              }}
            >
              Try a different account
            </a>
          </section>
          <LoginFooter />
        </div>
      </main>
    </>
  )
}

function ProviderLogo({ provider }: { provider: LoginProvider['provider'] }) {
  if (provider === 'password')
    return (
      <div className="sso-internal-logo">
        <CanopyMark size={32} />
        <strong>Canopy</strong>
      </div>
    )
  if (provider === 'google')
    return (
      <div className="sso-google-logo" aria-label="Google">
        <span>G</span>
        <span>o</span>
        <span>o</span>
        <span>g</span>
        <span>l</span>
        <span>e</span>
      </div>
    )
  if (provider === 'microsoft')
    return (
      <div className="sso-microsoft-logo">
        <span className="sso-microsoft-mark" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </span>
        <span>Microsoft</span>
      </div>
    )
  return (
    <div className="sso-internal-logo">
      <IdentityImage
        src="/identity/orgs/wayfound-hr.png"
        name="Wayfound HR"
        organization
      />
      <div>
        <strong>WAYFOUND HR</strong>
        <span>YOUR CONNECTED WORKSPACE</span>
      </div>
    </div>
  )
}

function LoginScreen() {
  const identity = useIdentity()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [provider, setProvider] = useState<LoginProvider | null>(null)
  const [discovering, setDiscovering] = useState(false)
  const [startingSso, setStartingSso] = useState(false)
  const [localError, setLocalError] = useState('')
  const pendingEmail = identity.ssoPending?.email
  useEffect(() => {
    if (pendingEmail) setEmail((current) => current || pendingEmail)
  }, [pendingEmail])
  const busy = discovering || startingSso || identity.busy
  const error = localError || identity.error
  const ssoWaiting = identity.desktopSsoAvailable && !!identity.ssoPending
  const oidcSignIn = provider?.authentication === 'oidc'
  const mockSignIn =
    provider?.mock_sso === true ||
    (provider?.authentication === undefined &&
      identity.state?.mock_sso_enabled === true &&
      provider?.provider !== 'password')
  const passwordSignIn =
    provider?.authentication === 'password' || provider?.provider === 'password'
  const screenProvider = passwordSignIn ? 'password' : provider?.provider
  const desktopError = (reason: unknown) =>
    (reason as Error).message.replace(
      /^Error invoking remote method '[^']+': (?:Error: )?/,
      '',
    )
  const cancelPendingSso = async () => {
    if (!ssoWaiting) return true
    try {
      await identity.cancelDesktopSso()
      return true
    } catch (reason) {
      setLocalError(desktopError(reason))
      return false
    }
  }
  const backToEmail = async () => {
    if (!(await cancelPendingSso())) return
    setProvider(null)
    setPassword('')
    setLocalError('')
    identity.dismissError()
    identity.dismissSsoNotice()
  }
  const discover = async () => {
    if (ssoWaiting) {
      setLocalError('Finish or cancel the Google sign-in in progress.')
      return
    }
    setDiscovering(true)
    setLocalError('')
    identity.dismissError()
    identity.dismissSsoNotice()
    try {
      setProvider(await api.discoverLogin(email.trim().toLowerCase()))
    } catch (reason) {
      setLocalError((reason as Error).message)
    } finally {
      setDiscovering(false)
    }
  }
  const signIn = async () => {
    if (!provider) return
    setLocalError('')
    if (oidcSignIn) {
      setStartingSso(true)
      try {
        if (identity.desktopSsoAvailable) {
          await identity.startDesktopSso(provider.email)
          setStartingSso(false)
          return
        }
        const { authorization_url } = await api.startSso(provider.email)
        const authorization = new URL(authorization_url)
        if (
          authorization.protocol !== 'https:' ||
          authorization.hostname !== 'accounts.google.com'
        )
          throw new Error('Google sign-in could not be started.')
        window.location.assign(authorization.href)
      } catch (reason) {
        setLocalError(desktopError(reason))
        setStartingSso(false)
      }
      return
    }
    try {
      await identity.login(provider.email, password)
      setPassword('')
    } catch {
      setPassword('')
    }
  }
  return (
    <main
      className={`identity-screen identity-login ${provider ? `sso-screen sso-${screenProvider}` : ''}`}
    >
      <div className="identity-screen-center">
        <section
          className={`identity-card ${provider ? 'sso-card' : 'identity-email-card'}`}
        >
          {provider ? (
            <>
              <ProviderLogo
                provider={passwordSignIn ? 'password' : provider.provider}
              />
              {!passwordSignIn && provider.provider === 'internal' && (
                <div className="sso-internal-bar">
                  <Icon name="shieldAlert" size={13} /> EMPLOYEE ACCESS GATEWAY
                  · v4.2
                </div>
              )}
              <div className="sso-card-body">
                <h1>
                  {passwordSignIn
                    ? 'Sign in to Canopy'
                    : provider.provider === 'google'
                      ? oidcSignIn
                        ? 'Sign in with Google'
                        : 'Welcome'
                      : provider.provider === 'microsoft'
                        ? 'Enter password'
                        : 'Authorized user login'}
                </h1>
                <button
                  className="sso-account"
                  disabled={busy}
                  onClick={() => void backToEmail()}
                  aria-label="Use a different email address"
                >
                  <Icon name="agent" size={15} />
                  <span>{provider.email}</span>
                  <Icon name="chevronDown" size={13} />
                </button>
                <p className="sso-description">
                  {passwordSignIn
                    ? 'Enter your Canopy password to open your agents and sessions.'
                    : provider.provider === 'internal'
                      ? 'Sign in to the Wayfound HR corporate network to access Canopy.'
                      : `Continue to Canopy with ${provider.provider === 'microsoft' ? 'Microsoft 365' : 'Google'}.`}
                </p>
                {oidcSignIn && provider.provider === 'google' && (
                  <p className="sso-email-hint">
                    Use the Google account for {provider.email}.
                  </p>
                )}
                {ssoWaiting && (
                  <p className="identity-sso-waiting" role="status">
                    Finish signing in with Google in your browser. You can
                    return to Canopy when it’s done.
                  </p>
                )}
                <form
                  onSubmit={(event) => {
                    event.preventDefault()
                    void signIn()
                  }}
                >
                  {!oidcSignIn && (
                    <label className="identity-field">
                      {!passwordSignIn && provider.provider === 'internal'
                        ? 'Network password'
                        : 'Password'}
                      <input
                        autoFocus
                        type="password"
                        autoComplete={mockSignIn ? 'off' : 'current-password'}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="Enter your password"
                        required
                        maxLength={1024}
                        disabled={busy}
                      />
                    </label>
                  )}
                  {error && (
                    <p className="identity-error" role="alert">
                      {error}
                    </p>
                  )}
                  {identity.ssoNotice && (
                    <p className="identity-status-note" role="status">
                      {identity.ssoNotice}
                    </p>
                  )}
                  <div className="identity-form-actions">
                    <button
                      className="identity-text-button"
                      type="button"
                      onClick={() => void backToEmail()}
                      disabled={busy}
                    >
                      {ssoWaiting ? 'Cancel sign-in' : 'Back'}
                    </button>
                    <button
                      type="submit"
                      className="identity-primary"
                      disabled={
                        busy || ssoWaiting || (!oidcSignIn && !password)
                      }
                    >
                      {startingSso
                        ? 'Opening browser…'
                        : identity.busy
                          ? 'Signing in…'
                          : ssoWaiting
                            ? 'Waiting for browser…'
                            : oidcSignIn
                              ? 'Continue with Google'
                              : provider.provider === 'internal'
                                ? 'Log on'
                                : provider.provider === 'google'
                                  ? 'Next'
                                  : 'Sign in'}
                      <Icon name="chevronRight" size={15} />
                    </button>
                  </div>
                </form>
              </div>
              {!passwordSignIn && provider.provider === 'internal' && (
                <div className="sso-internal-footer">
                  Wayfound HR · Internal use only
                </div>
              )}
            </>
          ) : (
            <>
              <div className="identity-login-logo">
                <CanopyLogo size={88} transparent />
              </div>
              <h1>
                {identity.addingAccount
                  ? 'Add another account.'
                  : 'Welcome to Canopy!'}
              </h1>
              {ssoWaiting && (
                <div className="identity-sso-waiting" role="status">
                  <p>
                    Finish Google sign-in for {identity.ssoPending?.email} in
                    your browser.
                  </p>
                  <button
                    className="identity-text-button"
                    type="button"
                    onClick={() => void cancelPendingSso()}
                  >
                    Cancel sign-in
                  </button>
                </div>
              )}
              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  void discover()
                }}
              >
                <label className="identity-field">
                  Work email
                  <input
                    autoFocus
                    type="email"
                    autoComplete="email"
                    spellCheck={false}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@company.com"
                    required
                    disabled={busy || ssoWaiting}
                  />
                </label>
                {error && (
                  <p className="identity-error" role="alert">
                    {error}
                  </p>
                )}
                {identity.ssoNotice && (
                  <p className="identity-status-note" role="status">
                    {identity.ssoNotice}
                  </p>
                )}
                <button
                  className="identity-primary identity-full-button"
                  type="submit"
                  disabled={busy || ssoWaiting || !email.trim()}
                >
                  {discovering
                    ? 'Finding your sign-in…'
                    : 'Continue with email'}
                  <Icon name="chevronRight" size={16} />
                </button>
              </form>
              <p className="identity-signin-note">
                You will be taken to your organization's login page.
              </p>
            </>
          )}
        </section>
        {identity.addingAccount && (
          <button
            className="identity-cancel"
            onClick={() => {
              void cancelPendingSso().then((canceled) => {
                if (canceled) identity.cancelAddAccount()
              })
            }}
            disabled={busy}
          >
            ← Back to {identity.state?.current_user?.name.split(' ')[0]}’s
            workspace
          </button>
        )}
        <LoginFooter />
      </div>
    </main>
  )
}

function OrganizationScreen() {
  const identity = useIdentity()
  const user = identity.state!.current_user!
  const organizations = identity.state!.organizations
  return (
    <main className="identity-screen">
      <WorkspaceBrand />
      <div className="identity-screen-center">
        <section className="identity-card organization-card">
          <div className="organization-person">
            <IdentityImage src={user.avatar_url} name={user.name} />
            <span>
              Signed in as <strong>{user.name}</strong>
              <small>{user.email}</small>
            </span>
          </div>
          <p className="identity-eyebrow">CHOOSE YOUR CONTEXT</p>
          <h1>Where are we working?</h1>
          <p className="identity-description">
            Your agents and past sessions stay organized in each workspace.
          </p>
          <div className="organization-list">
            {organizations.map((org) => (
              <button
                key={org.id}
                className="organization-choice"
                disabled={identity.busy}
                onClick={() =>
                  void identity.switchOrganization(org.id).catch(() => {})
                }
              >
                <IdentityImage
                  src={org.logo_url}
                  name={org.name}
                  organization
                />
                <span className="organization-choice-copy">
                  <strong>{org.name}</strong>
                  <small>
                    {org.id === user.default_org_id
                      ? 'Your default organization'
                      : 'Organization workspace'}
                  </small>
                </span>
                {org.id === user.default_org_id && (
                  <span className="identity-default-badge">Default</span>
                )}
                <Icon name="chevronRight" size={16} />
              </button>
            ))}
          </div>
          {!organizations.length && (
            <p className="identity-error" role="alert">
              This account has no available organizations. Contact your
              workspace administrator.
            </p>
          )}
          {identity.error && (
            <p className="identity-error" role="alert">
              {identity.error}
            </p>
          )}
          {identity.busy && (
            <p className="identity-status-note" role="status">
              Opening your organization…
            </p>
          )}
          <div className="organization-footer">
            <button
              className="identity-text-button"
              disabled={identity.busy}
              onClick={identity.addAccount}
            >
              Use another account
            </button>
            <button
              className="identity-text-button"
              disabled={identity.busy}
              onClick={() => void identity.logout().catch(() => {})}
            >
              Sign out
            </button>
          </div>
          {identity
            .state!.authorized_users.filter((account) => account.id !== user.id)
            .map((account) => (
              <button
                className="organization-signed-account"
                key={account.id}
                disabled={identity.busy}
                onClick={() =>
                  void identity.switchUser(account.id).catch(() => {})
                }
              >
                <IdentityImage src={account.avatar_url} name={account.name} />
                <span>Switch to {account.name}</span>
                <Icon name="chevronRight" size={14} />
              </button>
            ))}
        </section>
        <p className="identity-screen-footer">
          {user.is_superuser
            ? 'SUPER USER · ACCESS TO ALL ORGANIZATIONS'
            : 'YOUR ACCOUNT · YOUR ORGANIZATION'}
        </p>
      </div>
    </main>
  )
}

export function IdentitySwitcher({ compact = false }: { compact?: boolean }) {
  const identity = useIdentity()
  const [panel, setPanel] = useState<'users' | 'orgs' | null>(null)
  const container = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    if (!panel) return
    const outside = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setPanel(null)
    }
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPanel(null)
        trigger.current?.focus()
      }
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', onEscape)
    container.current
      ?.querySelector<HTMLButtonElement>('.identity-popover button')
      ?.focus()
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', onEscape)
    }
  }, [panel])
  if (!identity.state?.current_user || !identity.state.current_org) return null
  const {
    current_user: user,
    current_org: org,
    authorized_users,
    organizations,
  } = identity.state
  return (
    <div
      className={`identity-switcher ${compact ? 'is-compact' : ''}`}
      ref={container}
    >
      {panel && (
        <div
          className="identity-popover"
          role="dialog"
          aria-label={
            panel === 'users' ? 'Switch account' : 'Switch organization'
          }
        >
          <div className="identity-popover-heading">
            <span>
              {panel === 'users' ? 'SIGNED-IN ACCOUNTS' : 'YOUR ORGANIZATIONS'}
            </span>
            <button onClick={() => setPanel(null)} aria-label="Close switcher">
              <Icon name="close" size={14} />
            </button>
          </div>
          <div className="identity-popover-list">
            {panel === 'users'
              ? authorized_users.map((account) => (
                  <button
                    key={account.id}
                    className="identity-popover-option"
                    aria-pressed={account.id === user.id}
                    disabled={identity.busy}
                    onClick={() => {
                      setPanel(null)
                      if (account.id !== user.id)
                        void identity.switchUser(account.id).catch(() => {})
                    }}
                  >
                    <IdentityImage
                      src={account.avatar_url}
                      name={account.name}
                    />
                    <span>
                      <strong>{account.name}</strong>
                      <small>{account.email}</small>
                    </span>
                    {account.id === user.id && <Icon name="check" size={16} />}
                  </button>
                ))
              : organizations.map((organization) => (
                  <button
                    key={organization.id}
                    className="identity-popover-option"
                    aria-pressed={organization.id === org.id}
                    disabled={identity.busy}
                    onClick={() => {
                      setPanel(null)
                      if (organization.id !== org.id)
                        void identity
                          .switchOrganization(organization.id)
                          .catch(() => {})
                    }}
                  >
                    <IdentityImage
                      src={organization.logo_url}
                      name={organization.name}
                      organization
                    />
                    <span>
                      <strong>{organization.name}</strong>
                      {organization.id === user.default_org_id && (
                        <small>Default organization</small>
                      )}
                    </span>
                    {organization.id === org.id && (
                      <Icon name="check" size={16} />
                    )}
                  </button>
                ))}
          </div>
          {panel === 'users' && (
            <div className="identity-popover-actions">
              <button
                type="button"
                onClick={() => {
                  setPanel(null)
                  navigateWorkspace('/settings')
                }}
              >
                <Icon name="settings" size={15} />
                Settings
              </button>
              <button
                type="button"
                onClick={() => {
                  setPanel(null)
                  navigateWorkspace('/?connections=me')
                }}
              >
                <Icon name="attach" size={15} />
                Profile connections
              </button>
              <button
                onClick={() => {
                  setPanel(null)
                  identity.addAccount()
                }}
              >
                <Icon name="plus" size={15} />
                Add another account
              </button>
              <button
                onClick={() => {
                  setPanel(null)
                  void identity.logout().catch(() => {})
                }}
              >
                <Icon name="end" size={15} />
                Sign out {user.name.split(' ')[0]}
              </button>
              {authorized_users.length > 1 && (
                <button
                  onClick={() => {
                    setPanel(null)
                    void identity.logout(true).catch(() => {})
                  }}
                >
                  Sign out all accounts
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <button
        className="identity-context-button identity-org-button"
        aria-label={`Organization: ${org.name}. Switch organization`}
        aria-expanded={panel === 'orgs'}
        aria-haspopup="dialog"
        onClick={(event) => {
          trigger.current = event.currentTarget
          setPanel(panel === 'orgs' ? null : 'orgs')
        }}
      >
        <IdentityImage src={org.logo_url} name={org.name} organization />
        <span>
          <small>ORGANIZATION</small>
          <strong>{org.name}</strong>
        </span>
        <Icon name="chevronDown" size={15} />
      </button>
      <button
        className="identity-context-button identity-user-button"
        aria-label={`Account: ${user.name}. Switch account`}
        aria-expanded={panel === 'users'}
        aria-haspopup="dialog"
        onClick={(event) => {
          trigger.current = event.currentTarget
          setPanel(panel === 'users' ? null : 'users')
        }}
      >
        <IdentityImage src={user.avatar_url} name={user.name} />
        <span>
          <strong>{user.name}</strong>
          <small>{user.is_superuser ? 'Super user' : user.email}</small>
        </span>
        <Icon name="chevronDown" size={15} />
      </button>
    </div>
  )
}
